import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { produce } from 'immer';
import { formatNumber } from 'lib/pluralise';
import { filterArrayEmpty } from 'lib/riskbase';
import { FontFamilyRegular, theme } from 'lib/theme';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { PieDatum } from 'types/Reports';
import { VictoryPie } from 'victory-native';

type PieProps = {
  title: string;
  data: PieDatum[];
  onShowInGrid?: (datum: PieDatum) => void;
  size?: number;
};

export const PieChart: React.FC<PieProps> = ({
  title,
  data,
  onShowInGrid,
  size = 250,
}) => {
  const [hiddenSegments, setHiddenSegments] = React.useState<
    Record<(typeof data)[number]['key'], boolean>
  >({});

  const handleToggleSegment = (key: (typeof data)[number]['key']) => {
    setHiddenSegments((existing) =>
      produce(existing, (draft) => {
        if (existing[key]) {
          delete draft[key];
        } else {
          draft[key] = true;
        }
      }),
    );
  };

  const showPieLabels = size >= 200;

  return (
    <Card
      // label={title}
      contentStyle={{
        // height: 250,
        backgroundColor: 'white',
        // flexDirection: 'row',
      }}
      margin={false}
      marginHorizontal={false}
    >
      <View style={{ flexDirection: 'row' }}>
        <View
          style={{
            justifyContent: 'center',
          }}
        >
          <VictoryPie
            height={size}
            width={size}
            padding={theme.paddingHorizontal}
            style={{
              parent: {
                alignSelf: 'center',
              },
              labels: {
                fontFamily: FontFamilyRegular,
                fontSize: 13,
                fill: '#ffffff',
              },
            }}
            innerRadius={size / 10}
            labelRadius={() => size / 10 + (size / 10 - 8)}
            colorScale={data.map((datum) =>
              datum.color ? `#${datum.color}` : '',
            )}
            data={data.map((datum) => ({
              x:
                !showPieLabels || hiddenSegments[datum.key]
                  ? ' '
                  : [
                      datum.displayValue || datum.displayValue === 0
                        ? formatNumber(datum.displayValue)
                        : null,
                      datum.displayPercent || datum.displayPercent === 0
                        ? `${formatNumber(datum.displayPercent)}%`
                        : null,
                    ].join('\n'),
              y: hiddenSegments[datum.key] ? 0 : datum.value,
            }))}
            animate={{
              duration: 350,
              animationWhitelist: ['data'],
            }}
            // dataComponent={
            //   <Slice
            //     events={{
            //       onClick: () => {
            //       },
            //     }}
            //   />
            // }
          />
        </View>
        <ScrollView
          centerContent
          contentContainerStyle={{
            padding: theme.paddingHorizontal,
            paddingLeft: 0,
          }}
        >
          <Card
            margin={false}
            marginHorizontal={false}
            borderRadiusTop={false}
            borderRadiusBottom={false}
            withShadow={false}
          >
            {data.map((datum) => (
              <CardRow
                key={datum.label}
                label={datum.label}
                value={[
                  datum.displayValue || datum.displayValue === 0
                    ? formatNumber(datum.displayValue)
                    : null,
                  datum.displayPercent || datum.displayPercent === 0
                    ? `${formatNumber(datum.displayPercent)}%`
                    : null,
                ]
                  .filter(filterArrayEmpty)
                  .join(' • ')}
                icon={
                  hiddenSegments[datum.key]
                    ? ICONS.CircleXmarkLight
                    : ICONS.CircleSolid
                }
                iconColor={datum.color ? `#${datum.color}` : theme.colors.black}
                labelAlignTop
                contentStyle={{
                  paddingTop: theme.paddingHorizontalHalf,
                  paddingBottom: theme.paddingHorizontalHalf,
                }}
                hideMenuButton
                showMenuOnPress
                menuOptions={[
                  onShowInGrid
                    ? {
                        text: 'Filter Grid',
                        icon: ICONS.ListLight,
                        onPress: () => onShowInGrid(datum),
                      }
                    : null,
                  {
                    text: hiddenSegments[datum.key]
                      ? 'Show Segment'
                      : 'Hide Segment',
                    icon: hiddenSegments[datum.key]
                      ? ICONS.EyeLight
                      : ICONS.EyeSlashLight,
                    onPress: () => handleToggleSegment(datum.key),
                  },
                ].filter(filterArrayEmpty)}
              />
            ))}
          </Card>
        </ScrollView>
      </View>
      <View
        style={{
          paddingVertical: theme.paddingHorizontalHalf,
          paddingHorizontal: theme.paddingHorizontal,
          backgroundColor: theme.colors.background50,
        }}
      >
        <Txt
          numberOfLines={1}
          weight="bold"
          size="xsmall"
          style={{ color: theme.colors.fieldValueText }}
        >
          {title}
        </Txt>
      </View>
    </Card>
  );
};
